import React, { useEffect, useState } from 'react';

import { Box, Button, UiDialog as Dialog } from '@sunwisesoftware/sunwise-ui';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { POLYGON_SEGMENT } from 'common/components/segment/constants';
import RenderInputsPerSystem from 'common/components/segment/RenderInputsPerSystem';
import RoofAndSystemTypeSelects from 'common/components/segment/RoofAndSystemTypeSelects';
import { arraytoDictionary } from 'common/utils/helpers';
import {
    getMinMaxBuildHeight,
    getMinMaxCeilTilt,
    getMinMaxFrontBaseHeight,
    getMinMaxOuterRailLength,
    getMinMaxSeamDistance,
} from 'common/utils/helpers/systemsValues';
import yupResolver from 'common/utils/yupResolver';

import createProposalModal from '../../createProposalModal';
import * as multiPanelLayoutActions from '../../multiPanelLayout/actions';
import * as profileSelectors from '../../profile/selectors';
import * as systemsSelectors from '../../systems/selectors';
import * as actions from '../actions';
import * as selectors from '../selectors';

import validate from './forms/validateAddSegmentForm';

const AddSegmentFormContainer = ({
    createSegment,
    fetchPreset,
    fetchSystemTypes,
    isOpen,
    offerInfoPaternData,
    parentFieldSegmentData,
    presetsData,
    presetScope,
    roofTypes,
    setAddSegmentModalOpenStatus,
    systemTypes,
}) => {
    const { t } = useTranslation();
    const [presets, setPresets] = useState([]);
    const [minBuildHeight] = getMinMaxBuildHeight();
    const [minOuterRailLength] = getMinMaxOuterRailLength();
    const [minFrontBaseHeight] = getMinMaxFrontBaseHeight();

    const { control, handleSubmit, reset, setValue } = useForm({
        defaultValues: {
            building_height: minBuildHeight,
            category: '',
            ceil_tilt: 0,
            drawing_mode: `${POLYGON_SEGMENT}`,
            fill_algorithm: 'fill',
            quantity: 0,
            seam_distance: '',
            sheet_gauge: '',
            sloof_type: '',
            system: '',
            system_uid: '',
            type_application: '',
            rail_spacing: { strategy: 'automatic', value: [] },
            rail_support_length: { strategy: 'automatic', value: '' },
            plate_support_length: { strategy: 'automatic', value: '' },
            beam_spacing: { strategy: 'automatic', value: '' },
            support_length: { strategy: 'automatic', value: '' },
            lfoot_supports_width: { strategy: 'automatic', value: '' },
            gclamp_type: '',
            outer_rail_length: {
                strategy: 'automatic',
                value: minOuterRailLength,
            },
            front_base_height: {
                strategy: 'automatic',
                value: minFrontBaseHeight,
            },
        },
        resolver: yupResolver(validate),
    });

    useEffect(() => {
        if (presetScope === 'COMPANY')
            setPresets(presetsData.filter((p) => p.is_preset_company));
        else setPresets(presetsData.filter((p) => !p.is_preset_company));
    }, [presetScope]);

    const [presetId, system_uid, type_application] = useWatch({
        control,
        name: ['preset_configuration', 'system_uid', 'type_application'],
    });

    useEffect(() => {
        if (presetId) fetchPreset(presetId);
    }, [presetId]);

    const systemsDictionary = arraytoDictionary(systemTypes, 'id');
    const presetsDictionary = arraytoDictionary(presets, 'system_uniqueid');

    const { getRoofTypesOptions, getSystemTypesOptions } =
        createProposalModal.helpers;

    const onClose = () => {
        setAddSegmentModalOpenStatus(false);
        reset();
    };

    const onChangeTypeApplication = (e) => {
        const type = e.target.value;

        setValue('ceil_tilt', 0);
        setValue('sloof_type', '');
        setValue('system', '');
        setValue('system_uid', '');

        setValue('rail_spacing', { strategy: 'automatic', value: [] });
        setValue('rail_support_length', { strategy: 'automatic', value: '' });
        setValue('plate_support_length', { strategy: 'automatic', value: '' });
        setValue('beam_spacing', { strategy: 'automatic', value: '' });
        setValue('support_length', { strategy: 'automatic', value: '' });
        setValue('lfoot_supports_width', { strategy: 'automatic', value: '' });
        setValue('gclamp_type', '');
        setValue('outer_rail_length', {
            strategy: 'automatic',
            value: minOuterRailLength,
        });
        setValue('front_base_height', {
            strategy: 'automatic',
            value: minFrontBaseHeight,
        });

        if (!isEmpty(type)) fetchSystemTypes(type);
    };

    const onChangeSystem = (e) => {
        const system = e.target.value;
        const newSystemUid = systemsDictionary[system]?.uniqueid || '';
        const preset = presetsDictionary[newSystemUid];
        const presetConfiguration = preset?.preset_configuration || null;
        setValue('system_uid', newSystemUid);

        const [minCeilTilt] = getMinMaxCeilTilt(newSystemUid);
        const [minSeamDistance] = getMinMaxSeamDistance(newSystemUid);
        setValue('ceil_tilt', minCeilTilt);
        setValue('seam_distance', minSeamDistance);

        if (presetConfiguration && !isEmpty(presetConfiguration)) {
            setValue('preset_configuration', preset?.id);
            Object.entries(presetConfiguration).forEach(
                ([fieldName, fieldConfig]) => {
                    setValue(fieldName, fieldConfig);
                },
            );
        }
    };

    const onSubmit = (values) => {
        createSegment(values);
        setAddSegmentModalOpenStatus(false);
        reset();
    };

    useEffect(() => {
        setValue('tr50_cfe20', offerInfoPaternData?.tr50_cfe20 || 0);
        setValue('tr_10_cfe20', offerInfoPaternData?.tr_10_cfe20 || 0);
    }, [offerInfoPaternData]);

    useEffect(() => {
        setValue('panel', parentFieldSegmentData?.proposal?.panel || {});
    }, [parentFieldSegmentData]);

    return (
        <Dialog
            onClose={onClose}
            open={isOpen}
            size="sm"
            title={t('Add segment')}
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <RoofAndSystemTypeSelects
                    control={control}
                    onChangeSystem={onChangeSystem}
                    onChangeTypeApplication={onChangeTypeApplication}
                    roofTypesOptions={getRoofTypesOptions(roofTypes)}
                    systemTypesOptions={getSystemTypesOptions(systemTypes)}
                    systemUid={system_uid}
                    typeApplication={type_application}
                />

                <RenderInputsPerSystem
                    control={control}
                    isboom={true}
                    isNew={true}
                    systemUid={system_uid}
                />

                <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}
                >
                    <Button
                        onClick={onClose}
                        sx={{ mb: '0!important' }}
                        variant="text"
                    >
                        {t('Close')}
                    </Button>

                    <Button type="submit" sx={{ mb: '0!important' }}>
                        {t('Accept')}
                    </Button>
                </Box>
            </form>
        </Dialog>
    );
};

const mapStateToProps = createStructuredSelector({
    isOpen: selectors.getAddSegmentModalOpenStatus,
    offerInfoPaternData: selectors.getOfferInfoPaternData,
    parentFieldSegmentData: selectors.getParentFieldSegmentData,
    presetsData: systemsSelectors.getFetchPresetsData,
    presetScope: profileSelectors.getPresetScope,
    roofTypes: createProposalModal.selectors.getRoofTypesData,
    systemTypes: createProposalModal.selectors.getSystemTypesData,
});

const mapDispatchToProps = (dispatch) => ({
    createSegment: (values) => dispatch(actions.createSegment(values)),
    fetchPreset: (id) => dispatch(multiPanelLayoutActions.fetchPreset(id)),
    fetchSystemTypes: (type) =>
        dispatch(createProposalModal.actions.fetchSystemTypes(type)),
    setAddSegmentModalOpenStatus: (isOpen) =>
        dispatch(actions.setAddSegmentModalOpenStatus(isOpen)),
});

AddSegmentFormContainer.propTypes = {
    createSegment: PropTypes.func,
    fetchPreset: PropTypes.func,
    fetchSystemTypes: PropTypes.func,
    isOpen: PropTypes.bool.isRequired,
    offerInfoPaternData: PropTypes.object,
    parentFieldSegmentData: PropTypes.object,
    presetsData: PropTypes.array,
    presetScope: PropTypes.string,
    roofTypes: PropTypes.array,
    setAddSegmentModalOpenStatus: PropTypes.func,
    systemTypes: PropTypes.array,
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(AddSegmentFormContainer);
